<template>
  <div class="relative-motion-pro">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>v<sub>绝</sub> = v<sub>相</sub> + v<sub>牵</sub></div>
    </div>

    <div class="main-layout vertical">
      <!-- 仿真区域 -->
      <div class="simulation-stage dark-stage">
        <svg width="100%" height="200" viewBox="0 0 400 200">
          <rect x="0" y="40" width="400" height="120" fill="#0f172a" />
          <path v-for="i in 4" :key="'w'+i" :d="`M 0 ${40+i*24} Q 50 ${45+i*24} 100 ${40+i*24} T 200 ${40+i*24} T 300 ${40+i*24} T 400 ${40+i*24}`" 
                fill="none" stroke="rgba(59, 130, 246, 0.3)" stroke-width="1.5">
             <animate attributeName="d" :values="`M 0 ${40+i*24} Q 50 ${45+i*24} 100 ${40+i*24} T 200 ${40+i*24} T 300 ${40+i*24} T 400 ${40+i*24}; M -100 ${40+i*24} Q -50 ${45+i*24} 0 ${40+i*24} T 100 ${40+i*24} T 200 ${40+i*24} T 300 ${40+i*24}`" 
                      dur="3s" repeatCount="indefinite" />
          </path>
          <g :transform="`translate(${boatX}, ${boatY}) rotate(${angle})`">
             <path d="M -12,4 L 12,4 L 16,-4 L -16,-4 Z" fill="#334155" stroke="#f1f5f9" stroke-width="1" />
             <rect x="-4" y="-8" width="8" height="6" fill="#1e293b" />
             <line x1="0" y1="0" :x2="v_boat * 4" y2="0" stroke="#f59e0b" stroke-width="2" marker-end="url(#arrow-yellow)" v-if="running" />
          </g>
          <polyline :points="pathPoints" fill="none" stroke="#60a5fa" stroke-width="2" stroke-dasharray="4,4" />
        </svg>

        <div class="hud-panel">
           <div class="hud-item cyan">
              <label>参考系：{{ referenceFrame === 'ground' ? '地面 (静止)' : '水流 (运动)' }}</label>
              <span class="val">{{ v_abs.toFixed(1) }} <small>m/s</small></span>
           </div>
        </div>
      </div>

      <!-- 控制面板 -->
      <div class="compact-controls">
        <div class="control-card">
          <div class="control-vertical-group">
              <div class="mode-selector-row">
                 <span class="label">观测背景：</span>
                 <el-radio-group v-model="referenceFrame" size="small" class="full-row-radio">
                    <el-radio-button label="ground">地面参考系</el-radio-button>
                    <el-radio-button label="river">水流参考系</el-radio-button>
                 </el-radio-group>
              </div>
              
              <div class="control-grid-mini">
                 <div class="s-item">
                    <label>水流速 {{ v_river }}m/s</label>
                    <el-slider v-model="v_river" :min="0" :max="5" :step="0.1" size="small"></el-slider>
                 </div>
                 <div class="s-item">
                    <label>船速 {{ v_boat }}m/s</label>
                    <el-slider v-model="v_boat" :min="1" :max="10" :step="0.1" size="small"></el-slider>
                 </div>
                 <div class="s-item full">
                    <label>航向角 {{ angle }}°</label>
                    <el-slider v-model="angle" :min="-90" :max="90" size="small"></el-slider>
                 </div>
              </div>

              <div class="action-row">
                 <el-button type="primary" size="small" @click="toggle" icon="el-icon-video-play" :disabled="boatX > 400">开始启航</el-button>
                 <el-button size="small" plain @click="reset" icon="el-icon-refresh-right">重置实验</el-button>
              </div>
          </div>
        </div>

        <div class="discovery-card-compact" v-latex>
           <i class="el-icon-info"></i>
           <p v-if="referenceFrame === 'ground'"><strong>地面视角：</strong>我们看到船迹是合成后的方向（向右斜下方偏转）。这就是<strong>绝对运动</strong>。”</p>
           <p v-else><strong>随流视角：</strong>鱼儿看到船只仅仅是在水面上沿着船头指向直行。这就是<strong>相对运动</strong>。</p>
        </div>
      </div>
    </div>

    <svg height="0" width="0"><defs>
      <marker id="arrow-yellow" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto"><path d="M0,0 L6,3 L0,6 Z" fill="#f59e0b" /></marker>
    </defs></svg>
  </div>
</template>

<script>
export default {
  name: 'RelativeMotionViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      v_river: 2, v_boat: 5, angle: 0, referenceFrame: 'ground',
      running: false, boatX: 30, boatY: 100, history: [], timer: null
    }
  },
  computed: {
    v_abs() {
       const rad = (this.angle * Math.PI) / 180;
       const vx = this.v_boat * Math.cos(rad) + (this.referenceFrame === 'ground' ? this.v_river : 0);
       const vy = this.v_boat * Math.sin(rad);
       return Math.sqrt(vx * vx + vy * vy);
    },
    pathPoints() { return this.history.map(p => `${p.x},${p.y}`).join(' '); }
  },
  methods: {
    toggle() { 
      if (this.timer) cancelAnimationFrame(this.timer);
      this.running = !this.running; 
      if (this.running) this.animate(); 
    },
    animate() {
       if (this.timer) cancelAnimationFrame(this.timer);
       if (!this.running) return;
       this.timer = requestAnimationFrame(this.animate);

       const rad = (this.angle * Math.PI) / 180;
       const vx = this.v_boat * Math.cos(rad) + this.v_river;
       const vy = this.v_boat * Math.sin(rad);
       const dt = 0.08;
       this.boatX += vx * dt * 8; this.boatY += vy * dt * 8;
       
       if (this.referenceFrame === 'ground') { this.history.push({ x: this.boatX, y: this.boatY }); }
       if (this.boatX > 420 || this.boatY < 0 || this.boatY > 200) { this.running = false; }
    },
    reset() { 
      this.running = false; 
      this.boatX = 30; this.boatY = 100; 
      this.history = []; 
      if (this.timer) cancelAnimationFrame(this.timer);
      this.timer = null;
    }
  },
  beforeDestroy() { 
    this.running = false;
    if (this.timer) cancelAnimationFrame(this.timer); 
  }
}
</script>

<style scoped lang="scss">
.relative-motion-pro { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 13px; color: #1e293b; font-weight: 500; } .formula-chip { background: #f0fdf4; color: #16a34a; border: 1px solid #bbf7d0; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.simulation-stage { flex: 1; min-height: 200px; background: #020617; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; .hud-panel { position: absolute; top: 12px; left: 12px; display: flex; flex-direction: column; gap: 8px; .hud-item { background: rgba(15, 23, 42, 0.82); border-radius: 8px; border: 1px solid #1e293b; padding: 4px 12px; label { display: block; font-size: 10px; color: #94a3b8; font-weight: 700; } .val { font-size: 13px; color: #22d3ee; font-weight: 800; font-family: monospace; } &.cyan { border-left: 3px solid #06b6d4; } } } }
.compact-controls { display: flex; flex-direction: column; gap: 8px; }
.control-card { background: #ffffff; padding: 12px 16px; border-radius: 12px; border: 1px solid #e2e8f0; }
.control-vertical-group { display: flex; flex-direction: column; gap: 10px; .mode-selector-row { display: flex; align-items: center; gap: 10px; .label { font-size: 12px; color: #64748b; font-weight: 700; white-space: nowrap; } .full-row-radio { flex: 1; display: flex; } } .control-grid-mini { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; .s-item { label { font-size: 11px; color: #94a3b8; font-weight: 700; display: block; margin-bottom: 2px; } &.full { grid-column: span 2; } } } .action-row { display: flex; gap: 8px; border-top: 1px solid #f1f5f9; padding-top: 10px; .el-button { flex: 1; font-weight: 700; } } }
.discovery-card-compact { background: #f0f9ff; padding: 10px 14px; border-radius: 10px; border: 1px solid #e0f2fe; display: flex; align-items: center; gap: 10px; i { color: #0ea5e9; font-size: 1.1rem; } p { flex: 1; margin: 0; font-size: 12px; color: #0369a1; line-height: 1.5; strong { color: #075985; } } }
</style>
